import React from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Screen } from './Layout';
import { TopBar, BottomGnb, GnbItem } from './Controls';

/** 마지막으로 머문 하단 탭. 서브 화면에서 닫기를 누르면 이 자리로 돌아갑니다.
 *  예전에는 BottomNavigation 이 갖고 있었는데, 그 컴포넌트가 화면에서 빠지면서
 *  상수 하나 때문에 파일이 남아 있었습니다. 실제로 쓰는 이곳으로 옮깁니다. */
export const LAST_NAV_TAB_KEY = 'last_nav_tab';

/**
 * 앱 셸 — 상단바 · 하단 탭 · 화면 컨테이너를 한 곳에 묶습니다.
 *
 * 왜 필요한가
 *  Controls 의 TopBar / BottomGnb 는 순수 표현 컴포넌트라 화면마다 라우팅·i18n·
 *  뒤로가기 규칙을 직접 배선해야 합니다. 60개 화면을 그렇게 옮기면 화면마다
 *  동작이 갈라집니다. 여기서 한 번만 배선해 두고 화면은 의도만 선언합니다.
 *
 *      <AppScreen title="모임 상세" bar="sub"> ... </AppScreen>
 *      <AppScreen bar="none" gnb> ... </AppScreen>
 *
 * 기존 동작을 그대로 물려받습니다 (components/common/header/Header.tsx,
 * components/common/bottomNavigation/BottomNavigation.tsx 기준)
 *  · 뒤로가기 = navigate(-1), onBack 으로 override 가능
 *  · 닫기     = sessionStorage[LAST_NAV_TAB_KEY] || '/home' 으로 replace 이동
 *  · 탭       = /home · /functions · /chat · /settings, pathname 완전일치로 활성 판정,
 *               클릭 시 LAST_NAV_TAB_KEY 갱신 후 replace 이동
 *    네비게이션이 섞여 있어도 "마지막 탭" 기억이 어긋나지 않습니다.
 */

/* ── GNB 아이콘 — 기존 BottomNavigation 과 같은 도형 (이모지 금지) ── */

const IcHome = (on: boolean) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={on ? 2.4 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
);
const IcFeatures = (on: boolean) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={on ? 2.4 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
);
const IcChat = (on: boolean) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={on ? 2.4 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);
const IcSettings = (on: boolean) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={on ? 2.4 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);

/** 하단 탭 정의 — 기존 BottomNavigation 과 경로·순서·i18n 키 동일 */
const TAB_PATHS = ['/home', '/functions', '/chat', '/settings'] as const;

/* ══════════════════════════════════════════════════════════════════
 * AppGnb — 라우팅이 물린 하단 탭
 * ════════════════════════════════════════════════════════════════ */

interface AppGnbProps {
    /** 채팅 탭 알림 점 */
    chatDot?: boolean;
}

export const AppGnb = ({ chatDot }: AppGnbProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    // 탭 화면에 들어오면 "마지막 탭"을 기록 — 서브페이지 닫기(X)가 여기로 돌아옵니다
    React.useEffect(() => {
        if ((TAB_PATHS as readonly string[]).includes(location.pathname)) {
            sessionStorage.setItem(LAST_NAV_TAB_KEY, location.pathname);
        }
    }, [location.pathname]);

    const items: GnbItem[] = [
        { value: '/home', label: t('nav.home'), icon: IcHome },
        { value: '/functions', label: t('nav.features'), icon: IcFeatures },
        { value: '/chat', label: t('nav.chat'), icon: IcChat, dot: chatDot },
        { value: '/settings', label: t('nav.settings'), icon: IcSettings },
    ];

    const handleChange = (path: string) => {
        sessionStorage.setItem(LAST_NAV_TAB_KEY, path);
        navigate(path, { replace: true });
    };

    return <BottomGnb items={items} value={location.pathname} onChange={handleChange} />;
};

/* ══════════════════════════════════════════════════════════════════
 * AppBar — 라우팅이 물린 상단바
 * ════════════════════════════════════════════════════════════════ */

/** sub=뒤로+닫기 · close=닫기만 · plain=타이틀만 · none=상단바 없음 */
export type BarMode = 'sub' | 'close' | 'plain' | 'none';

interface AppBarProps {
    title?: string;
    mode?: BarMode;
    /** 뒤로가기 override — 기본은 navigate(-1) */
    onBack?: () => void;
    /** 닫기 override — 기본은 마지막 탭으로 replace 이동 */
    onClose?: () => void;
    /** 우측 커스텀 액션 (닫기 대신) */
    right?: React.ReactNode;
    /** 구분선 없이 배경에 녹임 */
    plainBorder?: boolean;
}

export const AppBar = ({
    title, mode = 'sub', onBack, onClose, right, plainBorder,
}: AppBarProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    if (mode === 'none') return null;

    const handleBack = () => {
        if (onBack) {
            onBack();
            return;
        }
        navigate(-1);
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
            return;
        }
        // 마지막으로 머문 탭으로 복귀 + 히스토리 초기화 (기존 Header 와 동일)
        const last = sessionStorage.getItem(LAST_NAV_TAB_KEY) || '/home';
        navigate(last, { replace: true });
    };

    return (
        <TopBar
            title={title}
            plain={plainBorder}
            backLabel={t('common.back')}
            closeLabel={t('common.close')}
            onBack={mode === 'sub' ? handleBack : undefined}
            onClose={mode === 'sub' || mode === 'close' ? handleClose : undefined}
            right={right}
        />
    );
};

/* ══════════════════════════════════════════════════════════════════
 * AppScreen — 화면 하나가 선언해야 할 전부
 * ════════════════════════════════════════════════════════════════ */

interface AppScreenProps {
    children: React.ReactNode;
    /** 상단 타이틀 */
    title?: string;
    /** 상단바 형태. 기본 sub(뒤로+닫기). 탭 화면은 'none' 또는 'plain' */
    bar?: BarMode;
    /** 하단 탭 표시 — 홈/기능/채팅/설정 화면만 true */
    gnb?: boolean;
    /** 채팅 탭 알림 점 */
    chatDot?: boolean;
    /** 하단 고정 버튼바가 있으면 true (여백 계산용) */
    hasFooter?: boolean;
    onBack?: () => void;
    onClose?: () => void;
    /** 상단바 우측 커스텀 액션 */
    right?: React.ReactNode;
    /** 화면 위에 떠 있는 것 (모임 만들기 버튼 등) */
    floating?: React.ReactNode;
}

export const AppScreen = ({
    children, title, bar = 'sub', gnb, chatDot, hasFooter, onBack, onClose, right, floating,
}: AppScreenProps) => (
    <Screen
        hasGnb={gnb}
        hasFooter={hasFooter}
        top={
            bar === 'none' ? undefined : (
                <AppBar
                    title={title}
                    mode={bar}
                    onBack={onBack}
                    onClose={onClose}
                    right={right}
                />
            )
        }
    >
        {children}
        {floating}
        {gnb ? <AppGnb chatDot={chatDot} /> : null}
    </Screen>
);
